// src/modules/PackageManager/CVEUpdateTab.tsx
import React, { useState, useEffect } from 'react';
import { Shield, Package, Building2, Loader2, Plus, X } from 'lucide-react';
import { updatePackageCVE, updateAllTenantCVEs, PackageCVEItem } from '../../components/services/PackageManager';
import api from '../../components/services/apiService';
import Project from '../../common/Utils/project';

interface Tenant {
  tenantId: string;
  tenantName: string;
  customerType: string;
  status: string;
}

const CVEUpdateTab: React.FC = () => {
  // Left section - Update multiple packages
  const [packages, setPackages] = useState<PackageCVEItem[]>([{ packageIdentifier: '', packageVersion: '' }]);
  const [isUpdatingPackage, setIsUpdatingPackage] = useState(false);
  const [packageUpdateMessage, setPackageUpdateMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Right section - Update all CVEs for tenant
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const [isUpdatingTenant, setIsUpdatingTenant] = useState(false);
  const [tenantUpdateMessage, setTenantUpdateMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Fetch tenants on component mount
  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    setIsLoadingTenants(true);
    try {
      const response = await api.get(`${Project.api}tenantservice/tenant/dashboard/tenants`);
      setTenants(response.data.data || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      setTenantUpdateMessage({
        type: 'error',
        text: 'Failed to load tenants. Please refresh the page.',
      });
    } finally {
      setIsLoadingTenants(false);
    }
  };

  const handlePackageChange = (index: number, field: 'packageIdentifier' | 'packageVersion', value: string) => {
    const updatedPackages = [...packages];
    updatedPackages[index][field] = value;
    setPackages(updatedPackages);
  };

  const addPackageRow = () => {
    setPackages([...packages, { packageIdentifier: '', packageVersion: '' }]);
  };

  const removePackageRow = (index: number) => {
    if (packages.length > 1) {
      const updatedPackages = packages.filter((_, i) => i !== index);
      setPackages(updatedPackages);
    }
  };

  const handleUpdatePackageCVE = async (e: React.FormEvent) => {
    e.preventDefault();

    // Filter out empty rows
    const validPackages = packages.filter((pkg) => pkg.packageIdentifier.trim() !== '' && pkg.packageVersion.trim() !== '');

    if (validPackages.length === 0) {
      setPackageUpdateMessage({ type: 'error', text: 'Please provide at least one package with identifier and version' });
      return;
    }

    setIsUpdatingPackage(true);
    setPackageUpdateMessage(null);

    try {
      const response = await updatePackageCVE({
        appsToUpdate: validPackages.map((pkg) => ({
          packageIdentifier: pkg.packageIdentifier.trim(),
          packageVersion: pkg.packageVersion.trim(),
        })),
      });

      if (response.isSuccess) {
        setPackageUpdateMessage({
          type: 'success',
          text: response.message || `Successfully updated CVE for ${validPackages.length} package(s)`,
        });
        setPackages([{ packageIdentifier: '', packageVersion: '' }]);
      } else {
        setPackageUpdateMessage({ type: 'error', text: response.message || 'Failed to update package CVE' });
      }
    } catch (error: any) {
      setPackageUpdateMessage({
        type: 'error',
        text: error?.response?.data?.message || 'An error occurred while updating package CVE',
      });
    } finally {
      setIsUpdatingPackage(false);
    }
  };

  const handleUpdateTenantCVEs = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTenantId) {
      setTenantUpdateMessage({ type: 'error', text: 'Please select a tenant' });
      return;
    }

    setIsUpdatingTenant(true);
    setTenantUpdateMessage(null);

    try {
      const response = await updateAllTenantCVEs({
        tenantId: selectedTenantId,
      });

      if (response.isSuccess) {
        setTenantUpdateMessage({ type: 'success', text: response.message || 'All tenant CVEs update initiated successfully' });
        setSelectedTenantId('');
      } else {
        setTenantUpdateMessage({ type: 'error', text: response.message || 'Failed to update tenant CVEs' });
      }
    } catch (error: any) {
      setTenantUpdateMessage({
        type: 'error',
        text: error?.response?.data?.message || 'An error occurred while updating tenant CVEs',
      });
    } finally {
      setIsUpdatingTenant(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-2">
        {/* <Shield className="text-red-500" size={28} /> */}
        <h2 className="text-2xl font-bold text-white">CVE Updates</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Section - Update Specific Packages */}
        <div className="bg-[#1C1C1D] p-6 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 mb-6">
            <Package className="text-red-400" size={24} />
            <h3 className="text-xl font-semibold text-white">Update Specific Packages</h3>
          </div>

          <form onSubmit={handleUpdatePackageCVE} className="space-y-4">
            <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
              {packages.map((pkg, index) => (
                <div key={index} className="bg-[#2A2A2B] p-4 rounded-lg border border-gray-600 space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-400">Package {index + 1}</span>
                    {packages.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePackageRow(index)}
                        className="text-red-400 hover:text-red-300 transition-colors"
                        disabled={isUpdatingPackage}
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Package Identifier *</label>
                    <input
                      type="text"
                      value={pkg.packageIdentifier}
                      onChange={(e) => handlePackageChange(index, 'packageIdentifier', e.target.value)}
                      placeholder="e.g., Mozilla.Firefox"
                      className="w-full px-3 py-2 bg-[#1C1C1D] border border-gray-600 rounded text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                      disabled={isUpdatingPackage}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Package Version *</label>
                    <input
                      type="text"
                      value={pkg.packageVersion}
                      onChange={(e) => handlePackageChange(index, 'packageVersion', e.target.value)}
                      placeholder="e.g., 143.0.3"
                      className="w-full px-3 py-2 bg-[#1C1C1D] border border-gray-600 rounded text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                      disabled={isUpdatingPackage}
                    />
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={addPackageRow}
              disabled={isUpdatingPackage}
              className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Plus size={16} />
              Add Another Package
            </button>

            {packageUpdateMessage && (
              <div
                className={`p-3 rounded-lg ${packageUpdateMessage.type === 'success' ? 'bg-green-900/30 border border-green-500/50 text-green-400' : 'bg-red-900/30 border border-red-500/50 text-red-400'}`}
              >
                <p className="text-sm">{packageUpdateMessage.text}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isUpdatingPackage}
              className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isUpdatingPackage ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Updating...
                </>
              ) : (
                'Update Package CVEs'
              )}
            </button>
          </form>
        </div>

        {/* Right Section - Update All Tenant CVEs */}
        <div className="bg-[#1C1C1D] p-6 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 mb-6">
            <Building2 className="text-red-400" size={24} />
            <h3 className="text-xl font-semibold text-white">Update All Tenant CVEs</h3>
          </div>

          <form onSubmit={handleUpdateTenantCVEs} className="space-y-4">
            <div>
              <label htmlFor="tenantSelect" className="block text-sm font-medium text-gray-300 mb-2">
                Select Tenant *
              </label>
              {isLoadingTenants ? (
                <div className="w-full px-4 py-2 bg-[#2A2A2B] border border-gray-600 rounded-lg flex items-center justify-center">
                  <Loader2 className="animate-spin text-gray-400" size={18} />
                  <span className="ml-2 text-gray-400">Loading tenants...</span>
                </div>
              ) : (
                <select
                  id="tenantSelect"
                  value={selectedTenantId}
                  onChange={(e) => setSelectedTenantId(e.target.value)}
                  className="w-full px-4 py-2 bg-[#2A2A2B] border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  disabled={isUpdatingTenant || tenants.length === 0}
                >
                  <option value="">-- Select a tenant --</option>
                  {tenants.map((tenant) => (
                    <option key={tenant.tenantId} value={tenant.tenantId}>
                      {tenant.tenantName} ({tenant.customerType})
                    </option>
                  ))}
                </select>
              )}
              {!isLoadingTenants && tenants.length === 0 && <p className="text-sm text-gray-400 mt-2">No tenants available</p>}
            </div>

            <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
              <p className="text-sm text-yellow-400">
                <strong>Note:</strong> This will update all available package CVEs for the specified tenant. This operation may take some
                time to complete.
              </p>
            </div>

            {tenantUpdateMessage && (
              <div
                className={`p-3 rounded-lg ${tenantUpdateMessage.type === 'success' ? 'bg-green-900/30 border border-green-500/50 text-green-400' : 'bg-red-900/30 border border-red-500/50 text-red-400'}`}
              >
                <p className="text-sm">{tenantUpdateMessage.text}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isUpdatingTenant || !selectedTenantId}
              className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isUpdatingTenant ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Updating...
                </>
              ) : (
                'Update All CVEs'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CVEUpdateTab;
